import {HTTP} from "@/utils/httpCommonUtil";
import {convertDateToUTC} from "@/utils/dateUtil";
import addMonths from "date-fns/add_months";
import GenericModal1 from '@/componentsReusable/genericModal.vue';
import GenericModal2 from '@/componentsReusable/genericModal.vue';
import {Issuable, IssuableController} from "@/components/provider/cards/issuables/IssuableModels";
import {getRestParamErrorMessage, getRestResponseErrorMessage} from "@/utils/serverErrorMessageUtil";
import flatPickr from "vue-flatpickr-component";
import {BallPulseLoader} from "vue-loaders";
import VueSimpleSpinner from 'vue-simple-spinner'
import {UserRoleEnum} from "@/enums/userRolesEnum";
import Vue from "vue";
import format from "date-fns/format";
import {issuableExpired, issuableExpiring} from "@/components/provider/cards/issuables/IssuableUtil";

const STALE_DATA_EVENT : string = 'staleData';

export default Vue.component('issuable-detail', {
  name : 'IssuableDetail',
  props : {
    issuable: {
      type: Object as () => Issuable
    },
    issuableController: {
      type: Object as () => IssuableController<any>
    }
  },

  components : {
    flatPickr,
    BallPulseLoader,
    VueSimpleSpinner,
    'generic-modal1' : GenericModal1,
    'generic-modal2' : GenericModal2
  },

  data () {
    return {
      isAuthorizedToDelete : false,
      savingNew : false,
      showConfirmationModal2 : false,
      pendingDeleteDocument : null,
      loadingData : false,
      showConfirmationModal1 : false,
    }
  },
  filters: {
    dateformat(dateISO: string) {
      return dateISO ? format(dateISO, 'M.D.YY') : 'NA';
    }
  },

  beforeMount() {
    this.isAuthorizedToDelete = this.$store.getters['user/hasRole']([UserRoleEnum.MANAGER]);
  },

  computed: {
    isProviderUser (): boolean {
      return this.$store.getters['user/hasOnlyRole'](UserRoleEnum.PROVIDER)
    },
    issuableExpired(): boolean {
      return issuableExpired(this.issuable, convertDateToUTC(new Date()));
    },
    issuableExpiring(): boolean {
      return issuableExpiring(this.issuable, convertDateToUTC(new Date()));
    },
  },

  methods: {
    async toggleEdit() {
      if (this.isProviderUser) {
        return;
      }
      this.issuable.isEditing = !this.issuable.isEditing;
      this.$emit('selected', this.issuable);
    },

    expirationChanged(dateStr: string) {
      if (!dateStr) {
        this.issuable.expirationNoticeSetting = 0;
        this.issuable.expirationNoticeDate = null;
      } else {
        this.expirationDateChange();
      }
    },

    expirationDateChange () {
      if (this.issuable.expirationNoticeSetting === 0 || !this.issuable.expirationDate) {
        this.issuable.expirationNoticeDate = null;
      } else {
        let expDate = convertDateToUTC(new Date(this.issuable.expirationDate!));
        this.issuable.expirationNoticeDate = addMonths(expDate, -this.issuable.expirationNoticeSetting);
      }
    },

    async associateUploadedFile (s3FilePath: string, credentialId: any, fileName: string) {
      const entityType = this.issuableController.entityType;
      try {
        const response = await HTTP.post('/api/attachment/', {
          "entityId": credentialId,
          "entityType": entityType,
          "attachmentName": fileName,
          "s3Key": s3FilePath
        });

        if (!response.data || !response.data.success) {
          throw new Error("Error associating uploaded file.");
        }
        this.issuable.attachments.push({
          id: response.data.data.attachmentId,
          name: fileName,
          s3Key: s3FilePath
        });
      } catch(err) {
        console.log('response Error!');
        console.log(err);
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred saving uploaded file attachment.'
        });
      }
    },

    async deleteDocument (id: any, iCredentialId: any, bypassDeleteConfirmation: boolean = false ) {
      if (!bypassDeleteConfirmation) {
        // Show Delete Confirmation Modal
        this.showConfirmationModal2 = true;
        this.pendingDeleteDocument = id;
        return;
      } else {
        this.pendingDeleteDocument = null;
      }
      let self = this;
      let showLoadingSpinnerTimeout = setTimeout(function () {
        self.loadingData = true;
      }, 0);
      try {
        await this.issuableController.deleteDocument(id);
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingData = false;
        this.$emit(STALE_DATA_EVENT);
      } catch (err) {
        console.log('response Error! ' + err);
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingData = false;
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: `An error occurred deleting ${this.issuableController.entityName} Document.`
        });
      }
    },

    async deleteConfirmed2 (id: any) {
      this.showConfirmationModal2 = false;
      await this.deleteDocument(this.pendingDeleteDocument, "0", true );
    },

    async save() {
      this.savingNew = true;
      try {
        await this.issuableController.createData(this.$route.params.providerId, this.issuable);
        this.savingNew = false;
        this.$emit(STALE_DATA_EVENT);
        this.$notify({
          group: 'main',
          type: 'success',
          title: 'Successful Save',
          text: ` ${this.issuableController.entityName} save successful.`,
          // duration: 5000
        });
      } catch (error) {
        this.savingNew = false;

        this.handleSaveError(error, "saving");
      }
    },

    async update () {
      this.issuable.updatingData = true;
      try {
        await this.issuableController.updateData(this.$route.params.providerId, this.issuable);
        this.issuable.updatingData = false;
        this.$emit(STALE_DATA_EVENT);
        this.$notify({
          group: 'main',
          type: 'success',
          title: 'Successful Update',
          text: `${this.issuableController.entityName} update successful.`,
        })
      } catch(error) {
        this.issuable.updatingData = false;
        this.handleSaveError(error, "updating");
      }
    },

    cancel() {
      this.issuable.isEditing = false;
      this.$emit('cancel', this.issuable);
    },

    async deleteIssuable(bypassDeleteConfirmation: boolean = false) {
      // To prevent a double submit
      if (this.issuable.updatingData) {
        console.log("returning."); return ;
      }

      if (!bypassDeleteConfirmation) {
        // Show Delete Confirmation Modal
        this.showConfirmationModal1 = true;
        return;
      }
      this.issuable.updatingData = true;
      try {
        await this.issuableController.deleteData(this.$route.params.providerId, this.issuable.id);
        this.issuable.updatingData = false;
        // deleteCredential.isEditing = false;
        this.$emit(STALE_DATA_EVENT);
        this.$notify({
          group: 'main',
          type: 'success',
          title: 'Successfully Deleted',
          text: `${this.issuableController.entityName} successfully deleted.`,
          // duration: 5000
        });
      } catch(error) {
        this.handleSaveError(error, "deleting")
      }
    },

    async deleteConfirmed1 () {
      this.showConfirmationModal1 = false;
      await this.deleteIssuable(true);
    },

    async openAttachment (s3Key: string) {
      // console.log("s3Key: " + s3Key);
      // get pre-signed url
      // open pre-signed url in a new tab
      try {
        const response = await HTTP.get('/api/s3/signed-url/get?s3key=' + s3Key);

        if (!response.data || !response.data.data) {
          throw new Error("Error getting attachment.");
        }
        console.log('signed URL: ' + response.data.data);
        window.open(response.data.data, '_blank');
      } catch(err) {
        console.log('response Error!');
        console.log(err);
        // console.log(err.message);
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred opening the file attachment.'
        });
      }
    },

    handleSaveError(error : any, verb : string) {
      this.issuableController.setErrorsOnDynamicFields(this.issuable, error);
      this.issuable.issuedDateErrorMessage = getRestParamErrorMessage(error, 'issuedDate');
      this.issuable.expirationDateErrorMessage = getRestParamErrorMessage(error, 'expirationDate');
      this.issuable.expirationNoticeDateErrorMessage = getRestParamErrorMessage(error, 'expirationNoticeDate');

      if (
        !this.issuableController.errorsExistOnDynamicFields(this.issuable) &&
        !this.issuable.issuedDateErrorMessage.length &&
        !this.issuable.expirationDateErrorMessage.length &&
        !this.issuable.expirationNoticeDateErrorMessage.length) {
        let generalError = getRestResponseErrorMessage(error);
        this.$notify({
          group: 'main',
          type: 'error',
          title: `Error ${verb}`,
          text: generalError ? generalError : `An error occurred ${verb} ${this.issuableController.entityName}.`,
          // duration: 5000
        });
      }
    }
  }
});



// WEBPACK FOOTER //
// ./src/components/provider/cards/issuables/IssuableDetail.ts